import * as React from 'react';
import { OnlineStudyPlanContext } from '../Context/OnlineStudyPlanContext';
import { GetCreditsLabel, GetSubjectsTotalPoints, GetSubjectsTotalPrice } from '../Context/OnlineStudyPlanHelpers';
import { Txt } from '../Txt';
import { Link } from '~/Features/Partials/Link/Link';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import { moneyToString } from '~/Partials/Utils/utils';
import styles from './SubjectsBasket.module.css';
import { useSubjectsBasketModel } from './SubjectsBasketModel';

export interface SubjectsBasketProps {
    setMobileBasketVisibility: (e: boolean) => void;
}

export const SubjectsBasket: React.FC<React.PropsWithChildren<SubjectsBasketProps>> = ({ ...props }) => {
    const { choicesFromBasket, pointsInBasketExceededWarningThreshold } = React.useContext(OnlineStudyPlanContext);
    const model = useSubjectsBasketModel();

    const subjectsFromChoices = choicesFromBasket.map(c => c.subject);
    const totalPoints = GetSubjectsTotalPoints(subjectsFromChoices);
    const totalPrice = GetSubjectsTotalPrice(subjectsFromChoices);
    const creditsLabel = GetCreditsLabel(subjectsFromChoices);

    return (
        <div className={styles.SubjectsBasket} id="subjectBasket">
            <div className={styles.Inner}>
                <div className={styles.Header}>
                    <h2 className={styles.Title}>
                        <Txt id="youHaveChosen" />
                    </h2>
                    <Button onClick={() => props.setMobileBasketVisibility(false)} className={styles.CloseMobileBasket}>
                        <SvgIcon iconName="close" />
                        <p className="visuallyHidden">
                            <Txt id="closeMobileBasket" />
                        </p>
                    </Button>
                </div>
                {choicesFromBasket.length > 0 && (
                    <ul className={styles.OrderList}>
                        {choicesFromBasket.map(choice => (
                            <li key={choice.subject.code} className={styles.OrderItem}>
                                <div className={styles.ItemName}>
                                    <Link
                                        label={choice.subject.name}
                                        href={choice.subject.url}
                                        className={styles.Link}
                                    />
                                </div>
                                <Button
                                    onClick={() => model.removeChoicesFromBasket(choice)}
                                    className={styles.ItemRemove}
                                >
                                    <SvgIcon className={styles.ItemRemoveIcon} iconName="circle-minus" />
                                    <span className="visuallyHidden">
                                        <Txt id="remove" />
                                    </span>
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className={styles.BasketInfo}>
                    {totalPoints > pointsInBasketExceededWarningThreshold && (
                        <span className={styles.ErrorMessage}>
                            <Txt
                                id="pointsInBasketExceededWarning"
                                values={{
                                    credits: pointsInBasketExceededWarningThreshold,
                                    creditsName: creditsLabel,
                                }}
                            />
                        </span>
                    )}
                    <span className={styles.BasketPoints}>
                        <Txt
                            id="totalPointsInfo"
                            values={{
                                credits: totalPoints,
                                creditsName: creditsLabel,
                            }}
                        />
                    </span>
                    <span className={styles.BasketPrice}>{moneyToString(totalPrice)}</span>
                </div>
            </div>
            <Button
                onClick={model.orderChoicesFromBasket}
                htmltype="submit"
                className={styles.SubmitButton}
                type={ButtonType.Solid}
                tier={ButtonTier.LegacyPrimary}
                size={ButtonSize.Large}
            >
                <Txt id="nextStep" />
            </Button>
        </div>
    );
};
